// ABOUTME: Reusable sidebar component for displaying content in a slide-in panel
// ABOUTME: Wraps Dialog with consistent styling, backdrop blur, and close button

import type { FC, ReactNode } from "react"
import { Suspense } from "react"
import { Dialog, DialogContent, DialogClose, DialogTitle } from "./CDialog"
import { CSpinner } from "./CSpinner"
import { cn } from "@shared/lib/utils"
import { useZIndex } from "@shared/hooks/useZIndex"
import { X } from "lucide-react"

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  width?: "lg" | "xl" | "full"
  padding?: boolean
}

const widthClasses = {
  lg: "w-[80vw] max-w-[80vw] max-md:w-[100vw] max-md:max-w-[100vw]",
  xl: "w-[90vw] max-w-[90vw] max-md:w-[100vw] max-md:max-w-[100vw]",
  full: "w-[95vw] max-w-[95vw] max-md:w-[100vw] max-md:max-w-[100vw]",
}

export const Sidebar: FC<SidebarProps> = ({ isOpen, onClose, children, width = "full", padding = false }) => {
  const zIndex = useZIndex(isOpen)

  if (!isOpen) return null

  return (
    <Suspense fallback={<CSpinner typeHeight="screen" />}>
      {/* Custom backdrop - closes sidebar on click */}
      <div
        // Not a control: the Dialog below owns Escape, so with the mouse this only repeats it.
        role="presentation"
        className="fixed inset-0 bg-black/50"
        style={{ zIndex }}
        onClick={onClose}
      />
      <Dialog
        open={isOpen}
        onOpenChange={(open: boolean) => !open && onClose()}
        modal={false}
      >
        <DialogContent
          className={cn(
            "fixed right-0 top-0 bottom-0 left-auto h-screen translate-x-0 translate-y-0 rounded-none border-l bg-gray-100 p-0",
            "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
            widthClasses[width],
          )}
          style={{ zIndex: zIndex + 1 }}
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">Sidebar</DialogTitle>
          <DialogClose className="absolute right-2 top-2 z-10 p-2 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-100 data-[state=open]:text-gray-500 cursor-pointer">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
          <div className={cn("bg-gray-100 min-h-screen overflow-y-auto", padding && "p-6")}>{children}</div>
        </DialogContent>
      </Dialog>
    </Suspense>
  )
}
